import {ComponentType, memo, useEffect, useMemo, useRef, useState} from 'react';

import styled, {css} from 'styled-components';
import {useSelector} from 'react-redux';

import {selectUserData, selectUserLoading} from '~/shared/store/selectors';
import {pushRoute, useLocation} from '~/shared/router';
import {SimpleSpinnerLoader} from '~/shared/components/Loaders';
import {createLogger} from '~/shared/logging';
import ManagerProvider from '~/shared/managers/ManagerProvider';
import {trackEvent} from '~/shared/services/analytics';
import {FlexCenter, flexColumn} from '~/shared/theme/FlexLayout';
import {FormNamesEnum} from '~/shared/services/analytics/analyticsModels';
import {media} from '~/shared/theme/media';
import {isJustEat} from '~/shared/config';
import {useIsMinLargeMobile} from '~/shared/hooks/deviceInfo';
import SignUp from '~/shared/components/LoginOrRegister/Register/SignUp';
import Login from '~/shared/components/LoginOrRegister/Login/Login';
import {
  AuthFlowStep,
  LOGIN_PROVIDER_ANALYTICS_MAP,
  LoginOrRegisterScreenKey,
  LoginProvider,
  ViewModes,
} from '~/shared/components/LoginOrRegister/consts';
import {
  convertQueryOrPropToStartingScreenKey,
  getStartingModeFromStartingScreenKey,
  handleSuccessAuthNavigation,
  isErrorWithMessage,
  onSubmitEmailHandler,
} from '~/shared/components/LoginOrRegister/utils/authHelpers';
import {UserCredentials} from '~/shared/store/storeModules/restrictedSharedActions';

import LoginOrRegisterHeader from './LoginOrRegisterHeader';
import RegisterAddressFinder from './RegisterAddressFinder';

const logger = createLogger('LoginOrRegister');

const screens: Record<LoginOrRegisterScreenKey, {
  key: LoginOrRegisterScreenKey;
  view: ComponentType<any>;
  modeName?: ViewModes;
}> = {
  [LoginOrRegisterScreenKey.LOGIN]: {
    key: LoginOrRegisterScreenKey.LOGIN,
    view: Login,
    modeName: ViewModes.SIGN_IN_MODE,
  },
  [LoginOrRegisterScreenKey.SIGN_UP]: {
    key: LoginOrRegisterScreenKey.SIGN_UP,
    view: SignUp,
    modeName: ViewModes.SIGN_UP_MODE,
  },
  [LoginOrRegisterScreenKey.ADDRESS_FINDER]: {
    key: LoginOrRegisterScreenKey.ADDRESS_FINDER,
    view: RegisterAddressFinder,
  },
};

const loginBoxShadow = css`
  box-shadow: ${({theme}) => theme.shadows.cardElevation};
`;

const LoginOrRegisterContainer = styled.div<{pageView: boolean; removeShadow: boolean}>`
  position: relative;
  ${flexColumn}
  background-color: ${({theme}) => theme.colors.surface};
  -webkit-overflow-scrolling: touch;
  width: 100%;
  max-width: 498px;
  margin: 0 auto;
  ${({pageView}) => !pageView && `
    overflow: auto;
    &&& {
      width: 505px;
    }
  `}
  ${({pageView, removeShadow}) => pageView && css`
    ${media.minLargeMobile`
      height: fit-content;
      border-radius: 16px;
      ${!removeShadow && loginBoxShadow}
    `}
  `}
`;

const LoaderOverlay = styled.div`
  position: absolute;
  background-color: ${({theme}) => theme.colors.surface};
  z-index: ${({theme}) => theme.zIndex.menuSubHeader};
  inset: 0;
  border-radius: 16px;
  ${media.minLargeMobile`
    ${loginBoxShadow}
  `}
`;

const LoaderContainer = styled(FlexCenter)`
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

type LoginOrRegisterWrapperProps = {
  pageView: boolean;
  onSuccess?: (userDetails: Awaited<ReturnType<typeof ManagerProvider.login>>) => void;
  backButtonUrl: string;
  closeModal?: () => void;
  isStrongLogin: boolean;
  fromCheckout: boolean;
  mode: string;
  scrollModalToBottom?: () => void;
};

type LoginOrRegisterProps = Omit<LoginOrRegisterWrapperProps, 'backButtonUrl' | 'mode'> & {
  currentScreenKey: LoginOrRegisterScreenKey;
  authFlowStep: AuthFlowStep;
};

const LoginOrRegister = (props: LoginOrRegisterProps) => {
  const {currentScreenKey, authFlowStep} = props;
  const screen = authFlowStep === AuthFlowStep.ADD_ADDRESS ? screens.addressFinder : screens[currentScreenKey];
  const {view: Component, modeName} = screen;

  return <Component {...props} mode={modeName} />;
};

const LoginOrRegisterWrapper = ({pageView, mode, onSuccess, backButtonUrl, closeModal, isStrongLogin, fromCheckout}: LoginOrRegisterWrapperProps) => {
  const isUserLoading = useSelector(selectUserLoading);
  const userData = useSelector(selectUserData);
  const isMinLargeMobile = useIsMinLargeMobile();
  const location = useLocation();
  const [currentScreenKey, setCurrentScreenKey] = useState(convertQueryOrPropToStartingScreenKey(location.routeParams.mode || mode) || (isJustEat ? screens.signup.key : screens.login.key));
  const providerDetails = useRef<Record<string, string | number | undefined>>();
  const [loginProvider, setLoginProvider] = useState(LoginProvider.TENBIS);
  const [providerViewMode, setProviderViewMode] = useState(getStartingModeFromStartingScreenKey(currentScreenKey));
  const [authFlowStep, setAuthFlowStep] = useState(AuthFlowStep.INITIAL);
  const [verificationDetails, setVerificationDetails] = useState<{authenticationToken: string; isPhoneMethod?: boolean} | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  const isRedirectedToStepRef = useRef(false);
  const [loading, setLoading] = useState(false);

  const isLoading = useMemo(() => isUserLoading || isSubmitting || loading, [isUserLoading, isSubmitting, loading]);

  const openLoginScreen = async ({email: emailVal, getAuthCodeForProviderConnect, providerProps}: {
    email?: string;
    getAuthCodeForProviderConnect?: boolean;
    providerProps?: Record<string, string | number | undefined>;
  } = {}) => {
    const authCodeResponse = getAuthCodeForProviderConnect ? await onSubmitEmailHandler({
      email: emailVal,
      shouldSendTrackEvent: true,
      setVerificationDetails,
      fromCheckout,
      loginProvider,
      setAuthFlowStep,
    }) : null;

    if (providerProps) {
      providerDetails.current = providerProps;
    }
    setLoginProvider(loginProvider);
    setProviderViewMode(ViewModes.SIGN_IN_MODE);
    setAuthFlowStep(authCodeResponse !== null ?
      authCodeResponse?.isPasswordScreen ?
        AuthFlowStep.PASSWORD : AuthFlowStep.OTP
      : AuthFlowStep.INITIAL,
    );
    setCurrentScreenKey(LoginOrRegisterScreenKey.LOGIN);
    if (emailVal) {
      setEmail(emailVal);
    }
  };

  const openSignUpScreen = () => {
    setProviderViewMode(ViewModes.SIGN_UP_MODE);
    setCurrentScreenKey(LoginOrRegisterScreenKey.SIGN_UP);
  };

  const openProviderScreen = ({providerProps, nextLoginProvider, nextScreen}: {
    providerProps: Record<string, string | number | undefined>;
    nextLoginProvider: LoginProvider;
    nextScreen: ViewModes;
  }) => {
    setCurrentScreenKey(LoginOrRegisterScreenKey.SIGN_UP);
    providerDetails.current = providerProps;
    setEmail(typeof providerProps.email === 'string' ? providerProps.email : null);
    setLoginProvider(nextLoginProvider);
    setProviderViewMode(nextScreen);
  };

  const onSubmit = async (values: UserCredentials & {userToken?: string}, linkType: string | null, providerFromArgs: LoginProvider) => {
    try {
      setIsSubmitting(true);
      const userDetails = await ManagerProvider.login(values, {onCheckout: fromCheckout, authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[providerFromArgs || loginProvider]});
      if (onSuccess) {
        onSuccess(userDetails);
      }
      handleSuccessAuthNavigation({pageView, closeModal, fromCheckout});
    } catch (error) {
      const errorType = 'login failed';
      logger.error(errorType, {error, fingerprint: errorType});

      const errorMessage = isErrorWithMessage(error) ? error.message : 'Unknown error';

      if (linkType) {
        trackEvent('hasFormError', {
          formType: '2FA Login Form',
          formErrorType: errorMessage,
          linkType,
        });
      }

      trackEvent('formErrorMessageView', {
        formName: linkType === '2FA - SMS' ? FormNamesEnum.OTP_LOGIN : FormNamesEnum.PASSWORD_LOGIN,
        errorType: errorMessage,
        onCheckout: fromCheckout,
        authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[providerFromArgs || loginProvider],
      });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (currentScreenKey === LoginOrRegisterScreenKey.LOGIN) {
      trackEvent('loginForm_view', {onCheckout: fromCheckout});
      return;
    }
    if (currentScreenKey === LoginOrRegisterScreenKey.SIGN_UP) {
      trackEvent('hasViewedRegistrationPopup', {onCheckout: fromCheckout});
    }
  }, [currentScreenKey, fromCheckout]);
  
  const onBackButtonClick = () => {
    if (pageView) {
      return pushRoute(backButtonUrl, {keepAllQueries: false});
    }
    if (closeModal) {
      closeModal();
    }
  };

  const onSubmitWithGoogleConnect = () => {
    onSubmit({}, null, LoginProvider.GOOGLE);
  };

  useEffect(() => {
    if (!isUserLoading && !!userData && !isStrongLogin && authFlowStep === AuthFlowStep.INITIAL) {
      handleSuccessAuthNavigation({pageView, closeModal});
    }
  }, [authFlowStep, isUserLoading, userData, isStrongLogin, pageView, closeModal]);

  return (
    <LoginOrRegisterContainer pageView={pageView} removeShadow={isLoading}>
      <LoginOrRegisterHeader
        viewMode={providerViewMode}
        authFlowStep={authFlowStep}
        setAuthFlowStep={setAuthFlowStep}
        onSignInClick={() => {
          trackEvent('hasClickedIHaveAnAccount');
          openLoginScreen();
        }}
        onSignUpClick={() => {
          trackEvent('hasClickedCreateAccount');
          openSignUpScreen();
        }}
        hideHeader={isStrongLogin || authFlowStep === AuthFlowStep.RESET_PASSWORD_SENT}
        onBackButtonClick={onBackButtonClick}
        isMinLargeMobile={isMinLargeMobile}
      />
      <LoginOrRegister
        {...{
          loginProvider,
          authFlowStep,
          setAuthFlowStep,
          providerViewMode,
          isSubmitting,
          setIsSubmitting,
          loading: isLoading,
          currentScreenKey,
          openLoginScreen,
          pageView,
          openSignUpScreen,
          onSubmit,
          onSuccess,
          onBackButtonClick,
          isStrongLogin,
          openProviderScreen,
          onSubmitWithGoogleConnect,
          ...(providerDetails.current || {}),
          email,
          setEmail,
          isRedirectedToStepRef,
          verificationDetails,
          setVerificationDetails,
          fromCheckout,
          closeModal,
          setLoading,
        }}
      />
      {isLoading && (
        <LoaderOverlay>
          <LoaderContainer>
            <SimpleSpinnerLoader variant="pieBig" />
          </LoaderContainer>
        </LoaderOverlay>
      )}
    </LoginOrRegisterContainer>
  );
};

export default memo(LoginOrRegisterWrapper);
